import type { PropertyAttributeRow, AttributeField } from "@/features/properties/types/propertyAttributes";
import { collectEvidenceUrls, getChildren } from "./treeHelpers";

/**
 * Creates and downloads a flat CSV with one row per (property × question).
 * Columns: Category | Question | Answer | Evidence
 * Cross-store exports additionally include Property | Property ID.
 *
 * This is the cross-store survey export per 7-Eleven spec §2.3 — a single
 * dataset suitable for data-lake ingestion (QuickBase, ServiceNow, etc.).
 */
export function exportPropertiesToCsv(
  propertyAttributes: PropertyAttributeRow[],
  properties: any[],
  projectName: string
) {
  const escapeCsv = (val: unknown): string => {
    if (val === null || val === undefined) return "";
    const str = String(val);
    if (str.includes(",") || str.includes('"') || str.includes("\n") || str.includes("\r")) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const sortedProperties = [...properties].sort((a, b) =>
    (a.name || a.address || "").localeCompare(b.name || b.address || "")
  );
  const includePropertyColumns = sortedProperties.length > 1;

  const rows: string[] = [
    (includePropertyColumns
      ? ["Property", "Property ID", "Category", "Question", "Answer", "Evidence"]
      : ["Category", "Question", "Answer", "Evidence"]
    ).join(","),
  ];

  for (const property of sortedProperties) {
    const propertyLabel = property.name || property.address || `Prop-${property.id?.slice(0, 4)}`;
    const propertyId = property.id || "";
    const attributeRow = propertyAttributes.find((pa) => pa.property_id === property.id);

    if (!attributeRow || !attributeRow.data) {
      // Still emit a row so cross-store exports show properties with no survey data.
      rows.push((includePropertyColumns
        ? [escapeCsv(propertyLabel), escapeCsv(propertyId), "", "", "", ""]
        : ["", "", "", ""]
      ).join(","));
      continue;
    }

    let fields: AttributeField[] = [];
    if (typeof attributeRow.data === "string") {
      try {
        fields = JSON.parse(attributeRow.data);
      } catch (e) {
        console.error("Failed to parse attribute data for", propertyId, e);
        continue;
      }
    } else {
      fields = attributeRow.data;
    }

    function processField(field: AttributeField, parentCleanTitle: string) {
      const children = getChildren(field);
      if (children) {
        const newParent = parentCleanTitle ? `${parentCleanTitle} > ${field.title}` : field.title;
        children.forEach((child) => processField(child, newParent));
        return;
      }

      let answerValue = "";
      switch (field.type) {
        case "photos":
          answerValue = field.value && field.value.length > 0 ? `${field.value.length} photo(s) attached` : "";
          break;
        case "area": {
          const raw = typeof field.value === "string" ? field.value : "";
          answerValue = raw.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
          break;
        }
        case "multiSelect":
          answerValue = field.value && field.value.length > 0 ? field.value.join("; ") : "";
          break;
        case "input":
        case "select": {
          const val = field.value;
          answerValue = val === null || val === undefined ? "" : String(val);
          break;
        }
        case "node":
          answerValue = "";
          break;
        default: {
          // Leaf types the union omits (date/number/text/switch/…) — stringify
          // the value instead of blanking the cell.
          const v = (field as { value?: unknown }).value;
          answerValue = v == null ? "" : String(v);
          break;
        }
      }

      const category = parentCleanTitle.split(" > ")[0] || "General";
      const cleanQuestion = field.title.replace(/\s*\[.*?\]\s*$/, "");

      rows.push((includePropertyColumns
        ? [
            escapeCsv(propertyLabel),
            escapeCsv(propertyId),
            escapeCsv(category),
            escapeCsv(cleanQuestion),
            escapeCsv(answerValue),
            escapeCsv(collectEvidenceUrls(field).join("; ")),
          ]
        : [
            escapeCsv(category),
            escapeCsv(cleanQuestion),
            escapeCsv(answerValue),
            escapeCsv(collectEvidenceUrls(field).join("; ")),
          ]
      ).join(","));
    }

    fields.forEach((field) => processField(field, ""));
  }

  const csvContent = rows.join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${projectName.replace(/[^a-z0-9]/gi, "_").toLowerCase()}_site_survey_data.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
